import { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Wand2, AlertTriangle, Unlink, CheckCircle2, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// All platforms xchannels supports (channel dropdown per project)
const PLATFORMS = [
  'linkedin', 'linkedin_artikel', 'instagram', 'facebook', 'x', 'twitter',
  'pinterest', 'youtube', 'threads', 'tiktok', 'bluesky',
];

const STATUS_STYLES = {
  connected: { label: 'Verbunden', className: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  missing: { label: 'Fehlt bei PostPeer', className: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
  unmapped: { label: 'Nicht gemappt', className: 'bg-slate-500/15 text-slate-300 border-slate-500/30' },
  needs_reauth: { label: 'Reauth nötig', className: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  unknown: { label: 'Unbekannt', className: 'bg-slate-500/15 text-slate-300 border-slate-500/30' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_STYLES[status] || STATUS_STYLES.unknown;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${cfg.className}`}
      data-testid={`postpeer-status-${status}`}
    >
      {cfg.label}
    </span>
  );
}

export default function PostPeerMapping() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [search, setSearch] = useState('');
  const [proposals, setProposals] = useState([]);
  const [proposing, setProposing] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data: resp } = await axios.get(`${API}/integrations/postpeer/admin/mapping`, {
        headers: getAuthHeader(),
      });
      setData(resp);
    } catch (e) {
      toast.error(`Konnte Mapping nicht laden: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const integrationsById = useMemo(() => {
    const map = {};
    (data?.integrations || []).forEach(i => { map[i.id] = i; });
    return map;
  }, [data]);

  // Integrations grouped by platform for the dropdowns
  const integrationsByPlatform = useMemo(() => {
    const grp = {};
    (data?.integrations || []).forEach(i => {
      if (!i.platform) return;
      (grp[i.platform] ||= []).push(i);
    });
    return grp;
  }, [data]);

  const filteredProjects = useMemo(() => {
    if (!data?.projects) return [];
    if (!search.trim()) return data.projects;
    const q = search.trim().toLowerCase();
    return data.projects.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.user_email || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const bindChannel = async (projectId, platform, postpeerAccountId) => {
    const key = `${projectId}:${platform}`;
    setSavingKey(key);
    try {
      await axios.put(
        `${API}/integrations/postpeer/admin/mapping/project/${projectId}/channel`,
        { platform, postpeer_account_id: postpeerAccountId || null },
        { headers: getAuthHeader() },
      );
      toast.success(postpeerAccountId ? 'Kanal verknüpft' : 'Verknüpfung aufgehoben');
      await load();
    } catch (e) {
      toast.error(`Fehler: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSavingKey(null);
    }
  };

  const bindProfile = async (projectId, postpeerProfileId) => {
    const key = `${projectId}:profile`;
    setSavingKey(key);
    try {
      await axios.put(
        `${API}/integrations/postpeer/admin/mapping/project/${projectId}/profile`,
        { postpeer_profile_id: postpeerProfileId || null },
        { headers: getAuthHeader() },
      );
      toast.success('PostPeer-Profil aktualisiert');
      await load();
    } catch (e) {
      toast.error(`Fehler: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSavingKey(null);
    }
  };

  const runAutoMatch = async () => {
    setProposing(true);
    try {
      const { data: resp } = await axios.post(
        `${API}/integrations/postpeer/admin/mapping/auto-match`,
        {},
        { headers: getAuthHeader() },
      );
      setProposals(resp.proposals || []);
      if (!resp.count) toast.info('Keine eindeutigen Auto-Match-Vorschläge gefunden.');
      else toast.success(`${resp.count} Vorschläge`);
    } catch (e) {
      toast.error(`Auto-Match Fehler: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setProposing(false);
    }
  };

  const applyProposal = async (proposal) => {
    await bindChannel(proposal.project_id, proposal.platform, proposal.postpeer_account_id);
    setProposals(prev => prev.filter(x => !(x.project_id === proposal.project_id && x.platform === proposal.platform)));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-300">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Lädt PostPeer-Mapping...
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 text-white" data-testid="postpeer-mapping-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold" data-testid="postpeer-mapping-title">
            PostPeer Mapping
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Verbinde xchannels-Projekte mit PostPeer-Accounts. Änderungen greifen sofort.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="border-slate-600 text-slate-100 hover:bg-slate-800"
            onClick={load}
            data-testid="postpeer-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Neu laden
          </Button>
          <Button
            className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-medium"
            onClick={runAutoMatch}
            disabled={proposing}
            data-testid="postpeer-automatch-btn"
          >
            {proposing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}
            Auto-Match nach Name
          </Button>
        </div>
      </div>

      {/* Counts */}
      {data?.counts && (
        <div className="flex flex-wrap gap-3 text-sm">
          <span className="px-3 py-1 rounded bg-slate-800/60 border border-slate-700" data-testid="count-projects">
            Projekte: <b>{data.counts.projects}</b>
          </span>
          <span className="px-3 py-1 rounded bg-slate-800/60 border border-slate-700" data-testid="count-integrations">
            PostPeer-Integrationen: <b>{data.counts.integrations}</b>
          </span>
          <span className="px-3 py-1 rounded bg-amber-500/10 border border-amber-500/40 text-amber-200" data-testid="count-unassigned">
            Nicht gemappt: <b>{data.counts.unassigned}</b>
          </span>
        </div>
      )}

      {/* Errors */}
      {data?.integrations_error && (
        <div className="p-3 rounded border border-rose-500/40 bg-rose-500/10 text-rose-200 text-sm flex items-center gap-2" data-testid="postpeer-error-banner">
          <AlertTriangle className="w-4 h-4" />
          PostPeer-Live-Liste nicht erreichbar: {data.integrations_error}. Anzeige nutzt gespeicherte Daten.
        </div>
      )}

      {/* Duplicate warnings */}
      {data?.duplicate_bindings && Object.keys(data.duplicate_bindings).length > 0 && (
        <div className="p-3 rounded border border-amber-500/40 bg-amber-500/10 text-amber-100 text-sm" data-testid="postpeer-duplicate-warning">
          <div className="flex items-center gap-2 font-medium mb-1">
            <AlertTriangle className="w-4 h-4" /> Ein PostPeer-Account ist an mehrere Projekte gebunden:
          </div>
          <ul className="list-disc ml-6 space-y-1">
            {Object.entries(data.duplicate_bindings).map(([accId, projs]) => (
              <li key={accId}>
                <span className="font-mono">{accId}</span> → {projs.map(p => p.project_name).join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Auto-match proposals */}
      {proposals.length > 0 && (
        <div className="p-4 rounded border border-cyan-500/40 bg-cyan-500/5" data-testid="automatch-proposals">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-cyan-200">Auto-Match-Vorschläge ({proposals.length})</div>
            <Button
              size="sm"
              variant="ghost"
              className="text-slate-400 hover:text-white"
              onClick={() => setProposals([])}
            >
              Verwerfen
            </Button>
          </div>
          <ul className="space-y-2">
            {proposals.map(p => (
              <li
                key={`${p.project_id}:${p.platform}`}
                className="flex flex-wrap items-center justify-between gap-2 p-2 bg-slate-800/60 rounded"
                data-testid={`proposal-${p.project_id}-${p.platform}`}
              >
                <div className="text-sm">
                  <b>{p.project_name}</b> · {p.platform} → <span className="text-cyan-300">{p.postpeer_display_name}</span>
                </div>
                <Button
                  size="sm"
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-900"
                  onClick={() => applyProposal(p)}
                  data-testid={`apply-proposal-${p.project_id}-${p.platform}`}
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Übernehmen
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Search + Layout */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Projekt oder E-Mail suchen…"
          className="pl-9 bg-slate-900 border-slate-700 text-white"
          data-testid="postpeer-search-input"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: Projects (2/3) */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-lg font-semibold text-slate-200">Projekte</h2>
          <div className="space-y-3">
            {filteredProjects.map(project => (
              <ProjectRow
                key={project.id}
                project={project}
                integrationsByPlatform={integrationsByPlatform}
                integrationsById={integrationsById}
                savingKey={savingKey}
                onBindChannel={bindChannel}
                onBindProfile={bindProfile}
              />
            ))}
            {filteredProjects.length === 0 && (
              <div className="text-slate-500 text-sm p-6 text-center border border-dashed border-slate-700 rounded">
                Keine Projekte für die Suche gefunden.
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Unassigned pool (1/3) */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-200">Nicht gemappte PostPeer-Accounts</h2>
          <div className="space-y-2">
            {(data?.unassigned || []).length === 0 ? (
              <div className="text-slate-500 text-sm p-6 text-center border border-dashed border-slate-700 rounded">
                Alle PostPeer-Accounts sind gemappt.
              </div>
            ) : (
              (data?.unassigned || []).map(i => (
                <div
                  key={i.id}
                  className="p-3 rounded bg-slate-800/60 border border-slate-700"
                  data-testid={`unassigned-${i.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-white truncate">{i.display_name}</div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        Plattform: <span className="text-slate-200">{i.platform}</span>
                      </div>
                      <div className="text-xs text-slate-500 font-mono mt-0.5 truncate">{i.id}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Project Row Component ────────────────────────────────────────────────────

function ProjectRow({ project, integrationsByPlatform, integrationsById, savingKey, onBindChannel, onBindProfile }) {
  const [expanded, setExpanded] = useState(project.channels.length > 0);
  const [addPlatform, setAddPlatform] = useState('linkedin');

  const existingPlatforms = new Set(project.channels.map(c => c.platform));
  const availablePlatforms = PLATFORMS.filter(p => !existingPlatforms.has(p));

  return (
    <div className="rounded border border-slate-700 bg-slate-900/60" data-testid={`project-row-${project.id}`}>
      <div
        className="p-3 flex items-center justify-between cursor-pointer hover:bg-slate-800/40"
        onClick={() => setExpanded(x => !x)}
      >
        <div className="min-w-0">
          <div className="font-medium text-white truncate" data-testid={`project-name-${project.id}`}>
            {project.name}
          </div>
          <div className="text-xs text-slate-500 truncate">
            {project.user_email || '—'} · <span className="font-mono">{project.id.slice(0, 8)}…</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {project.channels.length === 0 ? (
            <StatusBadge status="unmapped" />
          ) : (
            <span className="text-xs text-slate-400">
              {project.channels.length} Kanal{project.channels.length !== 1 ? 'e' : ''}
            </span>
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-800 p-3 space-y-3">
          {/* Profile */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-400 w-36 shrink-0">PostPeer Profile ID:</span>
            <Input
              defaultValue={project.postpeer_profile_id || ''}
              placeholder="(leer)"
              className="bg-slate-900 border-slate-700 text-white h-8 text-sm max-w-xs font-mono"
              data-testid={`profile-input-${project.id}`}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v !== (project.postpeer_profile_id || '')) onBindProfile(project.id, v || null);
              }}
              disabled={savingKey === `${project.id}:profile`}
            />
            {savingKey === `${project.id}:profile` && <Loader2 className="w-4 h-4 animate-spin text-slate-500" />}
          </div>

          {/* Existing channels */}
          {project.channels.map(ch => (
            <ChannelRow
              key={ch.platform}
              project={project}
              channel={ch}
              integrationsByPlatform={integrationsByPlatform}
              integrationsById={integrationsById}
              savingKey={savingKey}
              onBind={onBindChannel}
            />
          ))}

          {/* Add-new-channel row */}
          {availablePlatforms.length > 0 && (
            <div className="flex items-center gap-2 pt-1">
              <Select value={addPlatform} onValueChange={setAddPlatform}>
                <SelectTrigger
                  className="bg-slate-900 border-slate-700 text-white h-8 text-sm w-40"
                  data-testid={`add-platform-select-${project.id}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-white">
                  {availablePlatforms.map(p => (
                    <SelectItem
                      key={p}
                      value={p}
                      className="text-white focus:bg-slate-800 focus:text-white"
                    >
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <PlatformAccountSelect
                platform={addPlatform}
                currentAccountId={null}
                integrationsByPlatform={integrationsByPlatform}
                onChange={(newAccId) => newAccId && onBindChannel(project.id, addPlatform, newAccId)}
                testId={`add-account-select-${project.id}`}
                placeholder="Account wählen…"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChannelRow({ project, channel, integrationsByPlatform, integrationsById, savingKey, onBind }) {
  const isBusy = savingKey === `${project.id}:${channel.platform}`;
  return (
    <div
      className="flex items-center gap-2 flex-wrap p-2 rounded bg-slate-800/40"
      data-testid={`channel-row-${project.id}-${channel.platform}`}
    >
      <span className="w-28 text-sm font-medium text-slate-200">{channel.platform}</span>
      <PlatformAccountSelect
        platform={channel.platform}
        currentAccountId={channel.postpeer_account_id}
        integrationsByPlatform={integrationsByPlatform}
        onChange={(newAccId) => onBind(project.id, channel.platform, newAccId)}
        testId={`channel-select-${project.id}-${channel.platform}`}
      />
      <StatusBadge status={channel.status} />
      {channel.display_name && (
        <span className="text-xs text-slate-400 truncate max-w-[220px]">{channel.display_name}</span>
      )}
      {isBusy && <Loader2 className="w-4 h-4 animate-spin text-slate-500" />}
      <Button
        size="sm"
        variant="ghost"
        className="text-rose-300 hover:bg-rose-500/10"
        onClick={() => onBind(project.id, channel.platform, null)}
        disabled={isBusy}
        data-testid={`channel-unbind-${project.id}-${channel.platform}`}
      >
        <Unlink className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

function PlatformAccountSelect({ platform, currentAccountId, integrationsByPlatform, onChange, testId, placeholder = '— nicht gemappt —' }) {
  const options = integrationsByPlatform[platform] || [];
  const isOrphan = currentAccountId && !options.find(i => i.id === currentAccountId);
  const UNBOUND = '__unbound__';
  const selectValue = currentAccountId || UNBOUND;

  return (
    <Select
      value={selectValue}
      onValueChange={(v) => onChange(v === UNBOUND ? null : v)}
    >
      <SelectTrigger
        className="bg-slate-900 border-slate-700 text-white h-8 text-sm max-w-[280px] flex-1 min-w-[180px]"
        data-testid={testId}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-slate-900 border-slate-700 text-white max-h-[280px]">
        <SelectItem
          value={UNBOUND}
          className="text-slate-400 focus:bg-slate-800 focus:text-white"
        >
          {placeholder}
        </SelectItem>
        {options.map(i => (
          <SelectItem
            key={i.id}
            value={i.id}
            className="text-white focus:bg-slate-800 focus:text-white"
          >
            {`${i.display_name} (${i.id.slice(0, 8)}…)`}
          </SelectItem>
        ))}
        {isOrphan && (
          <SelectItem
            value={currentAccountId}
            className="text-rose-300 focus:bg-slate-800 focus:text-rose-200"
          >
            {`${currentAccountId} (fehlt bei PostPeer)`}
          </SelectItem>
        )}
        {options.length === 0 && !isOrphan && (
          <div className="px-2 py-3 text-xs text-slate-500">
            Keine {platform}-Accounts verfügbar.
          </div>
        )}
      </SelectContent>
    </Select>
  );
}
